import React, { useState } from "react";
import { Waveform, ArrowSquareOut, CaretDown, CaretUp, Play, X, SpeakerHigh } from "@phosphor-icons/react";
import { feedsForCounty, feedDetailUrl, feedEmbedUrl } from "../lib/scannerFeeds";

export default function ScannerFeedsCard({ countySlug, countyName }) {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(null); // feed id currently embedded
  const feeds = feedsForCounty(countySlug);

  if (!feeds || feeds.length === 0) return null;

  const toggleFeed = (id) => {
    setActiveId((prev) => (prev === id ? null : id));
  };

  return (
    <section
      data-testid="scanner-feeds-card"
      className="border-b border-tac-border bg-tac-surface/40"
    >
      <button
        type="button"
        data-testid="scanner-feeds-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-tac-surface/60 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Waveform size={14} weight="duotone" className="text-tac-info" />
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-text">
            Live Scanner
          </span>
          <span className="text-[15px] font-mono-tac tabular text-tac-faint">
            · {feeds.length}
          </span>
          {activeId && (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-tac-primary">
              <SpeakerHigh size={9} weight="fill" className="animate-pulse" /> live
            </span>
          )}
        </div>
        {open ? (
          <CaretUp size={12} weight="bold" className="text-tac-dim" />
        ) : (
          <CaretDown size={12} weight="bold" className="text-tac-dim" />
        )}
      </button>

      {open && (
        <>
          <ul className="divide-y divide-tac-border/50" data-testid="scanner-feeds-list">
            {feeds.map((f) => {
              const isActive = activeId === f.id;
              return (
                <li key={f.id} data-testid={`scanner-feed-${f.id}`} className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleFeed(f.id)}
                      data-testid={`scanner-play-${f.id}`}
                      className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${
                        isActive
                          ? "bg-tac-primary border-tac-primary text-white"
                          : "bg-tac-bg border-tac-border text-tac-text hover:border-tac-primary hover:text-tac-primary"
                      }`}
                      title={isActive ? "Hide player" : "Listen live"}
                    >
                      {isActive ? <X size={14} weight="bold" /> : <Play size={14} weight="fill" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold text-tac-text leading-tight">
                        {f.name}
                      </div>
                      {f.desc && (
                        <div className="text-[15px] font-mono-tac text-tac-faint mt-0.5">
                          {f.desc}
                        </div>
                      )}
                    </div>
                    <a
                      href={feedDetailUrl(f.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open on Broadcastify"
                      className="shrink-0 text-tac-faint hover:text-tac-info p-1"
                      data-testid={`scanner-external-${f.id}`}
                    >
                      <ArrowSquareOut size={12} weight="bold" />
                    </a>
                  </div>
                  {isActive && (
                    <div
                      className="mt-2 border border-tac-border rounded-sm overflow-hidden bg-black"
                      data-testid={`scanner-player-${f.id}`}
                    >
                      <iframe
                        title={`Broadcastify ${f.name}`}
                        src={feedEmbedUrl(f.id)}
                        width="100%"
                        height="140"
                        frameBorder="0"
                        allow="autoplay"
                        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="px-4 py-1.5 text-[10px] font-mono-tac text-tac-faint border-t border-tac-border/40">
            Live audio &copy; Broadcastify.com listeners
            {countyName ? ` · ${countyName} Co.` : ""}
          </div>
        </>
      )}
    </section>
  );
}
